import { useEffect, useState } from "react";
import { getMessagingInstance } from "lib/firebase";
import { getToken, onMessage } from "firebase/messaging";
import {
  getNotificationsFromIDB,
  saveNotificationToIDB,
  deleteNotificationFromIDB,
  toggleNotificationReadStatusInIDB,
  saveFcmToken,
  getFcmToken,
  setInitialSubscriptionDone,
  isInitialSubscriptionDone,
  saveSelectedTopics,
  getSelectedTopics,
} from "helpers";
import { topics } from "../data/topics";
import NotificationsContext from "./NotificationsContext";

export const NotificationsProvider = ({ children }) => {
  // Estado del modal
  const [isOpen, setIsOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [activeTab, setActiveTab] = useState(1);

  // Estado de notificaciones
  const [notifications, setNotifications] = useState([]);

  // Estado de configuración de topics
  const [selectedTopics, setSelectedTopics] = useState([]);

  // Estado de carga de topics
  const [isTopicsLoaded, setIsTopicsLoaded] = useState(false);

  // Estado del token FCM
  const [fcmToken, setFcmToken] = useState(null);

  // Estado de error
  const [error, setError] = useState(null);

  // Funciones del modal
  const openModal = () => setIsOpen(true);
  
  const closeModal = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      setIsOpen(false);
    }, 220);
  };

  // Funciones de notificaciones
  const deleteNotification = async (id) => {
    await deleteNotificationFromIDB(id);
    const updated = await getNotificationsFromIDB();
    setNotifications(updated);
  };

  const toggleReadStatus = async (id) => {
    await toggleNotificationReadStatusInIDB(id);
    const updated = await getNotificationsFromIDB();
    setNotifications(updated);
  };

  // Funciones de configuración de topics
  const toggleTopicSelection = async (slug, name) => {
    const isSelected = selectedTopics.includes(slug);
    
    // Limpiar error anterior
    setError(null);
    
    // Suscribir o desuscribir en Firebase si tenemos el token
    if (fcmToken) {
      let result;
      if (isSelected) {
        result = await unsubscribeFromTopics(fcmToken, [slug]);
      } else {
        result = await subscribeToTopics(fcmToken, [slug]);
      }
      
      // Solo actualizar el estado si la operación fue exitosa
      if (result?.success) {
        setSelectedTopics((prev) => {
          const next = isSelected
            ? prev.filter((item) => item !== slug)
            : [...prev, slug];
          saveSelectedTopics(next);
          return next;
        });
      } else {
        const errorMessage = `No se pudo ${isSelected ? 'desuscribir de' : 'suscribir a'} ${name}. Intentá nuevamente.`;
        setError(errorMessage);
      }
    }
  };

  // Suscripción a topics
  const subscribeToTopics = async (token, topicsToSubscribe) => {

    if (topicsToSubscribe.length === 0) {
      console.warn("⚠️ No se puede suscribir: topics vacíos.");
      return { success: false };
    }
    
    console.log("🔔 Suscribiendo token a topics:", topicsToSubscribe);
    try {
      const response = await fetch("/api/notifications/subscribe", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, topics: topicsToSubscribe }),
      });
      
      if (!response.ok && response.status !== 200) {
        console.error("❌ Error en la respuesta al suscribir:", response.statusText);
        return { success: false };
      }
      
      const data = await response.json();

      if (data?.error || data?.code !== 200) {
        console.error("❌ Error en la respuesta al suscribir (error en body):", data.error);
        return { success: false };
      }
      console.log("✅ Suscripción completada:", data);
      return { success: true, data };
    } catch (error) {
      console.error("❌ Error al suscribir a topics:", error);
      return { success: false, error };
    }
  };

  // Desuscripción de topics
  const unsubscribeFromTopics = async (token, topicsToUnsubscribe) => {
    if (topicsToUnsubscribe.length === 0) {
      console.warn("⚠️ No se puede desuscribir: topics vacíos.");
      return { success: false };
    }
    
    console.log("🔕 Desuscribiendo token de topics:", topicsToUnsubscribe);
    try {
      const response = await fetch("/api/notifications/unsubscribe", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, topics: topicsToUnsubscribe }),
      });
      
      if (!response.ok && response.status !== 200) {
        console.error("❌ Error en la respuesta al desuscribir:", response.statusText);
        return { success: false };
      }
      
      const data = await response.json();

      if (data?.error || data?.code !== 200) {
        console.error("❌ Error en la respuesta al suscribir (error en body):", data.error);
        return { success: false };
      }

      console.log("✅ Desuscripción completada:", data);
      return { success: true, data };
    } catch (error) {
      console.error("❌ Error al desuscribir de topics:", error);
      return { success: false, error };
    }
  };

  // Cargar notificaciones desde IndexedDB
  useEffect(() => {
    getNotificationsFromIDB().then(setNotifications);
  }, []);

  // Cargar topics seleccionados y token desde localStorage
  useEffect(() => {
    const storedTopics = getSelectedTopics();
    setSelectedTopics(storedTopics);
    
    const storedToken = getFcmToken();
    if (storedToken) {
      setFcmToken(storedToken);
    }
  }, []);

  // Configurar Firebase Messaging
  useEffect(() => {
    const messagingInstance = getMessagingInstance();
    if (typeof window !== "undefined" && messagingInstance) {
      navigator.serviceWorker
        .register("/firebase-messaging-sw.js")
        .then((registration) => {
          Notification.requestPermission().then((permission) => {
            if (permission === "granted") {
              getToken(messagingInstance, {
                vapidKey: process.env.NEXT_PUBLIC_FIREBASE_VAPID_KEY,
              })
                .then(async (currentToken) => {
                  if (currentToken) {
                    console.log("🚀 FCM Token obtenido:", currentToken);
                    
                    // Guardar token en estado y localStorage
                    setFcmToken(currentToken);
                    saveFcmToken(currentToken);
                    
                    // Verificar si es la primera vez
                    const alreadySubscribed = isInitialSubscriptionDone();
                    
                    // if (!alreadySubscribed) {
                    //   // Primera vez: suscribir a TODOS los topics
                    //   const allTopicSlugs = topics.map((topic) => topic.slug);
                    //   console.log("🎯 Primera suscripción: suscribiendo a todos los topics");
                      
                    //   const result = await subscribeToTopics(currentToken, allTopicSlugs);
                      
                    //   // Solo marcar como completado si la suscripción fue exitosa
                    //   if (result?.success) {
                    //     // Guardar en localStorage que todos están seleccionados
                    //     saveSelectedTopics(allTopicSlugs);
                    //     setSelectedTopics(allTopicSlugs);
                        
                    //     // Marcar que ya se hizo la suscripción inicial
                    //     setInitialSubscriptionDone();
                    //   } else {
                    //     console.error("❌ Falló la suscripción inicial, se reintentará en la próxima visita");
                    //   }
                      
                    //   // Marcar como cargado independientemente del resultado
                    //   setIsTopicsLoaded(true);
                    // } else {
                    //   console.log("✓ Suscripción inicial ya realizada previamente");
                    //   // Ya estaba suscrito, marcar como cargado inmediatamente
                    //   setIsTopicsLoaded(true);
                    // }
                  } else {
                    console.log(
                      "❌ No se pudo obtener el token de notificación. ¿Permiso denegado o VAPID key incorrecta?"
                    );
                  }
                })
                .catch((err) => {
                  console.error("Error al obtener el token de FCM:", err);
                });
            } else {
              console.warn("🚫 Permiso de notificación denegado o bloqueado.");
            }
          });
          
          onMessage(messagingInstance, (payload) => {
            const notificationToSave = {
              id: Date.now(),
              title: payload?.notification?.title || "",
              body: payload?.notification?.body || "",
              icon: payload?.notification?.icon || "/img/clarin-sharing.png",
              image: payload?.notification?.image,
              data: payload?.data || {},
              timestamp: new Date().toISOString(),
              read: false,
            };
            
            setNotifications((prev) => {
              const updated = [notificationToSave, ...prev].slice(0, 10);
              return updated;
            });

            saveNotificationToIDB(notificationToSave);
          });
        })
        .catch((error) => {
          console.error("❌ Fallo al registrar el Service Worker:", error);
        });
    }
  }, []);

  // Manejar el bloqueo del scroll cuando el modal está abierto
  useEffect(() => {
    if (isOpen) {
      setIsClosing(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const shouldLock = isOpen || isClosing;
    if (shouldLock) {
      document.body.style.overflow = "hidden";
      document.documentElement.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = "";
        document.documentElement.style.overflow = "";
      };
    }

    return undefined;
  }, [isOpen, isClosing]);

  const value = {
    // Estado del modal
    isOpen,
    isClosing,
    activeTab,
    setActiveTab,
    openModal,
    closeModal,
    
    // Notificaciones
    notifications,
    deleteNotification,
    toggleReadStatus,
    
    // Configuración de topics
    isTopicsLoaded,
    selectedTopics,
    toggleTopicSelection,
    topics,
    fcmToken,
    
    // Error
    error,
  };

  return (
    <NotificationsContext.Provider value={value}>
      {children}
    </NotificationsContext.Provider>
  );
};
